export type AccountLimitType =
  | 'follows'
  | 'unfollows'
  | 'messages'
  | 'open_profile'
  | 'inmail'
  | 'connection_requests'
  | 'profile_visits'
  | 'post_likes'
  | 'comments';

export type AccountDailyLimits = Record<AccountLimitType, number>;

export interface IAccountActivityStatus {
  featureEnabled: boolean;
  featureMode: 'legacy' | 'account' | 'disabled';
  businessOnly: boolean;
  timezone: string;
  defaultTimezone: string;
  resetHour: number;
  resetMinute: number;
  linkedinDay: string;
  resetsAt: string;
  dailyLimits: AccountDailyLimits;
  maxDailyLimits: AccountDailyLimits;
  usageToday: Partial<AccountDailyLimits>;
  connectionRequestsWeekly?: {
    limit: number;
    used: number;
    remaining: number;
    weekStartsAt?: string;
    weekResetsAt?: string;
  };
  connectionRequestNotesMonthly?: {
    isPremium: boolean;
    limit: number | null;
    used: number;
    remaining: number | null;
    monthResetsAt?: string;
    maxChars?: number;
    /** True when LinkedIn reported CUSTOM_INVITE_LIMIT_REACHED this calendar month. */
    linkedInNotesExhausted?: boolean;
  };
}

export interface IAccountLimitMeta {
  key: AccountLimitType;
  label: string;
  hint: string;
  /** When true, limit still applies server-side but is not shown in the settings panel. */
  hidden?: boolean;
}

export const ACCOUNT_LIMIT_META: IAccountLimitMeta[] = [
  { key: 'connection_requests', label: 'Connection requests', hint: 'Hard cap: 100 invitations per week per LinkedIn profile. Free accounts: max 5 personalized notes per month.' },
  { key: 'messages',            label: 'Messages',            hint: 'Direct messages to 1st-degree connections.' },
  { key: 'open_profile',        label: 'Open Profile',        hint: 'Free messages to Open Profile leads. Separate from paid InMail.' },
  { key: 'inmail',              label: 'InMail',              hint: 'Paid InMail messages. Uses LinkedIn credits. Separate from Open Profile and regular Messages.' },
  { key: 'profile_visits',      label: 'Profile visits',      hint: 'Profile views triggered by automation.' },
  { key: 'post_likes',          label: 'Post likes',          hint: 'Likes on prospect posts.' },
  { key: 'comments',            label: 'Comments',            hint: 'Comments on prospect posts.' },
  { key: 'follows',             label: 'Follows',             hint: 'Profile follow actions.' },
  { key: 'unfollows',           label: 'Unfollows',           hint: 'Profile unfollow actions.' },
];

/** Limits shown in the account-activity settings panel. */
export const VISIBLE_ACCOUNT_LIMIT_META = ACCOUNT_LIMIT_META.filter(m => !m.hidden);

/**
 * Step type → daily limit bucket.
 * sn_message maps to both Open Profile and paid InMail — use SN_MESSAGE_LIMIT_TYPES.
 */
export const STEP_TO_LIMIT_TYPE: Partial<Record<string, AccountLimitType>> = {
  connection_request: 'connection_requests',
  message:            'messages',
  profile_visit:      'profile_visits',
  post_like:          'post_likes',
  post_comment:       'comments',
};

/** Both buckets an sn_message step can consume (sendMethod decides which). */
export const SN_MESSAGE_LIMIT_TYPES: AccountLimitType[] = ['open_profile', 'inmail'];

/** All limit types relevant to a campaign sequence step. */
export function limitTypesForStep(stepType: string): AccountLimitType[] {
  if (stepType === 'sn_message') return [...SN_MESSAGE_LIMIT_TYPES];
  const one = STEP_TO_LIMIT_TYPE[stepType];
  return one ? [one] : [];
}
